<template>
    <v-form
        ref="formRef"
        class="report-form"
    >
        <div
            class="deepi-col gap-16"
        >
            <div
                class="deepi-row gap-16"
            >
                <ReportCanvas
                    :screenshot="props.screenshot"
                    @update:screenshot="screenshotFile = $event"
                />
                <div
                    class="deepi-col gap-4"
                >
                    <DeepiSelectField
                        :label="tr('report.properties.category')"
                        :rules="[
                            dropdownRules.required()
                        ]"
                        :items="categories"
                        v-model="category"
                    />
                    <DeepiTextField
                        :label="tr('report.properties.title')"
                        :rules="[
                            textRules.required()
                        ]"
                        v-model="title"
                    />
                    <DeepiTextArea
                        :label="tr('report.properties.description')"
                        :rows="10"
                        :rules="[
                            textRules.required()
                        ]"
                        v-model="description"
                    />
                    <div
                        class="deepi-col gap-16"
                    >
                        <div
                            class="deepi-row-fixed gap-16"
                        >
                            <div
                                class="deepi-col gap-4"
                            >
                                <p
                                    v-title-clamp
                                    class="text-body-m text-secondary-500 text-ellipsis"
                                >
                                    {{ tr('report.properties.browser') }}
                                </p>
                                <p
                                    v-title-clamp
                                    class="text-body-m text-secondary-500 text-ellipsis"
                                >
                                    {{ tr('report.properties.operating-system') }}
                                </p>
                                <p
                                    v-title-clamp
                                    class="text-body-m text-secondary-500 text-ellipsis"
                                >
                                    {{ tr('report.properties.window-size') }}
                                </p>
                            </div>
                            <div
                                class="deepi-col gap-4"
                            >
                                <p
                                    v-title-clamp
                                    class="text-body-m text-ellipsis"
                                >
                                    {{ browserInfo.name }} {{ browserInfo.version }}
                                </p>
                                <p
                                    v-title-clamp
                                    class="text-body-m text-ellipsis"
                                >
                                    {{ osInfo.name }} {{ osInfo.version }}
                                </p>
                                <p
                                    v-title-clamp
                                    class="text-body-m text-ellipsis"
                                >
                                    {{ windowWidth }} × {{ windowHeight }}
                                </p>
                            </div>
                        </div>
                        <p
                            v-title-clamp
                            class="text-body-m text-clamp-2"
                        >
                            {{ templateWindow.location.href }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </v-form>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import Bowser from 'bowser';
import { storeToRefs } from 'pinia';
import { type VForm } from 'vuetify/lib/components/index.mjs';

import { useRules, useWindow } from '@deepidoo/deesplay/composables';
import { useFetchClient } from '@deepidoo/playdoo/services/composables';
import { useOrganizationStore, useUserStore } from '@deepidoo/playdoo/stores';

import { useTranslations } from '../../composables';
import { Report, ReportCategory, type ReportDTO } from '../../models';

import ReportCanvas from '../reportCanvas/ReportCanvas.vue';

export interface ReportFormProps {
    screenshot?: File;
};

const props = defineProps<ReportFormProps>();

const { dropdownRules, textRules } = useRules();
const { windowHeight, windowWidth } = useWindow();
const { post, postWithFile } = useFetchClient();
const organizationStore = useOrganizationStore();
const { currentOrganization } = storeToRefs(organizationStore);
const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);
const { tr } = useTranslations();

const templateWindow = window as any;

const formRef = ref<VForm>();

const screenshotFile = ref<File | undefined>(props.screenshot);
const category = ref<ReportCategory>(ReportCategory.BUG);
const title = ref('');
const description = ref('');

const categories = computed((): { text: string; value: ReportCategory }[] => [{
    text: tr('report.category.bug'),
    value: ReportCategory.BUG
}, {
    text: tr('report.category.design'),
    value: ReportCategory.DESIGN
}, {
    text: tr('report.category.question'),
    value: ReportCategory.QUESTION
}, {
    text: tr('report.category.request'),
    value: ReportCategory.REQUEST
}, {
    text: tr('report.category.wording'),
    value: ReportCategory.WORDING
}]);

const browserInfo = computed(() => Bowser.getParser(templateWindow.navigator.userAgent).getBrowser());
const osInfo = computed(() => Bowser.getParser(templateWindow.navigator.userAgent).getOS());

defineExpose({
    validate: async (): Promise<Report | undefined> => {
        if (!screenshotFile.value || !formRef.value || !currentUser.value) {
            return;
        }
        await formRef.value.validate();
        if (!formRef.value.isValid) {
            return;
        }
        const screenshotResponse = await postWithFile<{ screenshotUrl: string }>(
            `${import.meta.env.VITE_REPORT_URL}screenshots`,
            screenshotFile.value,
            undefined,
            [{
                key: 'screenshotId',
                value: crypto.randomUUID()
            }]
        );
        if (!screenshotResponse || !screenshotResponse.screenshotUrl) {
            return;
        }
        const response = await post<{ data: { createReport: ReportDTO }}>(
            `${import.meta.env.VITE_REPORT_URL}graphql`,
            {
                query: `
                    mutation CreateReport($input: CreateReportDTO!) {
                        createReport(input: $input) {
                            id
                            status
                            category
                            title
                            description
                            pageUrl
                            screenshotUrl
                            userId
                            firstname
                            lastname
                            email
                            phone
                            phoneCountry
                            client
                            organization
                            settings {
                                browser
                                os
                                width
                                height
                            }
                        }
                    }
                `,
                variables: {
                    input: {
                        category: category.value,
                        title: title.value,
                        description: description.value,
                        pageUrl: templateWindow.location.href,
                        screenshotUrl: screenshotResponse.screenshotUrl,
                        userId: currentUser.value.id,
                        firstname: currentUser.value.firstname,
                        lastname: currentUser.value.lastname,
                        email: currentUser.value.email,
                        phone: currentUser.value.phone,
                        phoneCountry: currentUser.value.phoneCountry,
                        client: currentOrganization.value?.client.name,
                        organization: currentOrganization.value?.name,
                        settings: {
                            browser: browserInfo.value.name + ' ' + browserInfo.value.version,
                            os: osInfo.value.name + ' ' + osInfo.value.version,
                            width: windowWidth.value,
                            height: windowHeight.value
                        }
                    }
                }
            }
        );

        if (response.data && response.data.createReport) {
            return new Report(response.data.createReport);
        }
    }
});
</script>

<style scoped lang="scss">
@use 'ReportForm.scss';
</style>